import {
  MaterialUiColumnsProps,
  MaterialUiTable,
  usePersistMuiTableState,
  CheckOutlined,
  CloseOutlined,
  ButtonDS,
  Spacer,
  triggerToast,
} from "@qivia/ui";
import styled from "styled-components";
import { RelatedDriverRequestsDisplayed } from "./driversApi";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  driverSlice,
  relatedDriverRequestsListAsync,
  selectRelatedDriverRequestsList,
  selectRelatedDriverRequestsListStatus,
  selectUpdateRelatedDriverRequestStatus,
} from "./driversSlice";
import { useGridApiRef } from "@mui/x-data-grid-premium";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "../../i18n/i18n";
import { AssociateRelatedDriversModal } from "./AssociateRelatedDriversModal";

export const AssociateRelatedDriversTab = () => {
  const params = useParams();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const relatedDriverRequestsList = useAppSelector(
    selectRelatedDriverRequestsList,
  );
  const relatedDriverRequestsListStatus = useAppSelector(
    selectRelatedDriverRequestsListStatus,
  );
  const updateRelatedDriverRequestStatus = useAppSelector(
    selectUpdateRelatedDriverRequestStatus,
  );

  const [listDisplayed, setListDisplayed] = useState<
    RelatedDriverRequestsDisplayed[]
  >([]);

  const [modalState, setModalState] = useState<{
    isVisible: boolean;
    action: "ACCEPTED" | "REJECTED" | null;
    rowData: RelatedDriverRequestsDisplayed | null;
  }>({
    isVisible: false,
    action: null,
    rowData: null,
  });

  useEffect(() => {
    if (relatedDriverRequestsListStatus === "idle") {
      void dispatch(relatedDriverRequestsListAsync());
    }
  }, [dispatch, relatedDriverRequestsListStatus]);

  const relatedDriverRequestsListWithId = useMemo(
    () =>
      relatedDriverRequestsList.map((cd) => ({
        id: cd.uuid,
        uuid: cd.uuid,
        oldDriverName: cd.oldDriver.firstName + " " + cd.oldDriver.lastName,
        oldDriverStatus: cd.oldDriver.status,
        oldDriverDeletedAt: cd.oldDriver.deletedAt,
        newDriverName: cd.newDriver.firstName + " " + cd.newDriver.lastName,
        newDriverStatus: cd.newDriver.status,
        newDriverCreatedAt: cd.newDriver.createdAt,
      })),
    [relatedDriverRequestsList],
  );

  useEffect(() => {
    setListDisplayed(relatedDriverRequestsListWithId);
  }, [relatedDriverRequestsListWithId]);

  useEffect(() => {
    if (updateRelatedDriverRequestStatus === "success") {
      triggerToast(t("relatedDriverRequests.modal.success") || "", "valid");
      void dispatch(
        driverSlice.actions.resetUpdateRelatedDriverRequestStatus(),
      );
    } else if (updateRelatedDriverRequestStatus === "failed") {
      triggerToast(t("relatedDriverRequests.modal.failure") || "", "error");
      void dispatch(
        driverSlice.actions.resetUpdateRelatedDriverRequestStatus(),
      );
    }
  }, [dispatch, updateRelatedDriverRequestStatus, t]);

  const searchBarProps = {
    values: relatedDriverRequestsListWithId,
    setFilterValues: setListDisplayed,
    keysToIgnore: ["actions"],
  };

  const columnsDisplayed: {
    [index in keyof RelatedDriverRequestsDisplayed]: MaterialUiColumnsProps;
  } = useMemo(() => {
    return {
      uuid: {
        type: "string",
      },
      oldDriverName: {
        type: "string",
      },
      oldDriverStatus: {
        type: "string",
        width: 150,
      },
      oldDriverDeletedAt: {
        type: "dateTime",
        valueGetter: (value) => (value ? new Date(value) : null),
      },
      newDriverName: {
        type: "string",
      },
      newDriverStatus: {
        type: "string",
        width: 150,
      },
      newDriverCreatedAt: {
        type: "dateTime",
        valueGetter: (value) => new Date(value),
      },
      actions: {
        type: "actions",
        width: 250,
        renderCell: (params) => (
          <>
            <ButtonDS
              text={t("relatedDriverRequests.button.accepted.name")}
              format="hug"
              buttonType="primary"
              sizeButton="S"
              leftIcon={<CheckOutlined />}
              onClick={() =>
                setModalState({
                  isVisible: true,
                  action: "ACCEPTED",
                  rowData: params.row as RelatedDriverRequestsDisplayed,
                })
              }
            />
            <Spacer x={1} />
            <ButtonDS
              text={t("relatedDriverRequests.button.rejected.name")}
              format="hug"
              buttonType="danger"
              sizeButton="S"
              leftIcon={<CloseOutlined />}
              onClick={() =>
                setModalState({
                  isVisible: true,
                  action: "REJECTED",
                  rowData: params.row as RelatedDriverRequestsDisplayed,
                })
              }
            />
          </>
        ),
      },
    };
  }, [t]);

  const pageName = "relatedDriverRequests";
  const apiRef = useGridApiRef();
  const key = "customers-grid";
  const aggregation = {
    model: {
      amount: "avg",
      merchantName: "size",
      category: "size",
      driverName: "size",
    },
  };

  const dataGridState = usePersistMuiTableState(
    apiRef,
    key,
    pageName,
    aggregation,
  );

  if (params.tab !== "relatedDriverRequests") {
    return;
  }

  return (
    <StyledContainer>
      <AssociateRelatedDriversModal
        modalState={modalState}
        setModalState={setModalState}
      />
      <MaterialUiTable<
        keyof RelatedDriverRequestsDisplayed & { actions?: string },
        RelatedDriverRequestsDisplayed
      >
        rows={listDisplayed}
        columns={columnsDisplayed}
        pageName={pageName}
        dataGridState={dataGridState}
        searchBar={searchBarProps}
        apiRef={apiRef}
      />
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;
